import type {MATH_TYPE, PropertyType, ResourceType} from 'common/report/constants';
import type {Bucket, StringBucket} from 'common/widgets/bucket-pane/types';
import {Type} from 'common/query-builder/cohorts-query-builder/models/enums/builder-enums';
import type {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import type {SpecialTimeUnit, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import type {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import type {FilterOperator, FilterValue, ListQuantifier} from 'common/widgets/property-filter-menu/models/types';
import type {EventItem} from 'common/query-builder/query-block/events-menu/types';
import type {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import type {FiltersOperator} from 'common/query-builder/query-entry-section/types';
import {MultiAttribution} from 'common/widgets/multi-attribution-menu/types';

/**
 * Defines a behavioral computed property used in the Users page
 * and core reports for behavioral segmentation analysis
 */
export type Behavior = {
  aggregationOperator: MATH_TYPE;
  event: EventItem;
  dateRange?: Partial<DateRange>;
  property?: PropertySectionItem | null;
  /**
   * Using Segmentation[] rather than PropertyFilter[] type. Previously it was using PropertyFilter[], but this caused
   * serialization/deserialization issues when it was being pass out of mp-query-builder to Insights report and then
   * back into mp-query-builder.
   */
  filters: Segmentation[];
  filtersOperator: FiltersOperator;
  behaviorType?: string;
  startStepNumber?: number;
  endStepNumber?: number;

  multiAttribution?: MultiAttribution;
  mainDateRange?: Partial<DateRange>;
};

export const BehavioralAttributes: Array<keyof Behavior> = [
  `aggregationOperator`,
  `filtersOperator`,
  `event`,
  `dateRange`,
  `property`,
  `filters`,
];

export enum ProfileHistoryType {
  // Use historical profile properties and treat them exactly like event properties
  EventTime = `eventTime`,

  // Use historical profile properties allowing a time range
  // e.g. "users who did any event in <time range> and have this property value"
  // TODO(jakub): can this be less confusing?
  DateRange = `dateRange`,

  // Default behavior: use latest value of profile properties
  Latest = `latest`,
}

export const ProfileHistoryTypeToDisplayName: Record<ProfileHistoryType, string> = {
  [ProfileHistoryType.EventTime]: `As of Event Time`,
  [ProfileHistoryType.Latest]: `Latest Value`,
  [ProfileHistoryType.DateRange]: `Through Time`,
};

export type BasicProfileHistoryOptions = {
  type: ProfileHistoryType.EventTime | ProfileHistoryType.Latest;
};

export type DateRangeProfileHistoryOptions = {
  type: ProfileHistoryType.DateRange;
  dateRange: Partial<DateRange>;
};

export type ProfileHistoryOptions = BasicProfileHistoryOptions | DateRangeProfileHistoryOptions;

export function isDateRangeProfileHistoryOptions(
  options: ProfileHistoryOptions,
): options is DateRangeProfileHistoryOptions {
  return options?.type === ProfileHistoryType.DateRange;
}

/**
 * Segmentation defines a property filter or breakdown
 * this is the standard format expected by the UI on filters
 * cohorts, funnels, flows & retention APIs
 */
export type Segmentation = {
  customPropertyId?: number | string;
  customProperty?: CustomProperty;
  datasetId?: string;
  dataGroupId?: string;
  tempDataGroupId?: string;
  resourceType: ResourceType;
  propertyDefaultType: PropertyType;
  propertyName: string | string[];
  propertyObjectKey?: string | string[];
  propertyType: PropertyType;
  joinPropertyType?: PropertyType;
  unit?: TimeUnit | SpecialTimeUnit;
  customBucket?: Bucket;
  customGroups?: StringBucket;

  /**
   * Segmentation can support filtering for the ProjectFeatureFlag.SingleSegment
   * feature flag, in that mode, we populate filter fields to
   * represent a segment + filter based on UI state
   *
   * DEPRECATED: SingleSegment / Value Comparison project seems to have been deprecated (https://github.com/mixpanel/analytics/pull/47955)
   */
  filterValue?: FilterValue;
  filterOperator?: FilterOperator;
  filterDateUnit?: TimeUnit | SpecialTimeUnit;
  listItemFilters?: PropertyFilter[];
  filterJoinType?: Type;

  /** For list properties ([tech spec](https://www.notion.so/mxpnl/Breakdown-QApi-Implementation-d35e842315024db78e10f72c179878b2)) */
  listItemGroup?: Segmentation;
  listQuantifier?: ListQuantifier;

  /** For TTC breakdowns */
  startStepNumber?: number;
  endStepNumber?: number;

  /** Defines a computed behavioral property based on each user's event stream */
  behavior?: Behavior;

  /**
   * Temp value for slowly changing dimensions POC
   */
  scdEvent?: string;

  profileHistoryOptions?: ProfileHistoryOptions;
};

export type BehavioralSegmentation = Segmentation & Required<Pick<Segmentation, 'behavior'>>;

/** Specifies field members that define a given property selection on a filter or segment */
export const SegmentationPropertyFields = new Set<keyof Segmentation>([
  `tempDataGroupId`,
  `behavior`,
  `customPropertyId`,
  `customProperty`,
  `joinPropertyType`,
  `propertyObjectKey`,
  `propertyName`,
  `dataGroupId`,
  `scdEvent`,
]);

/**
 * References Insights API resource types
 *
 * @deprecated legacy Insights only way of defining resource types
 */
export enum CustomPropertyResourceType {
  Events = `events`,
  People = `people`,
}

/**
 * Defines a computed property
 */
export type CustomProperty = {
  name: string;
  description: string;
  exampleValue?: string;
  resourceType?: CustomPropertyResourceType;
  displayFormula?: string;
  composedProperties?: Record<string, TODO>;

  /** @deprecated behaviors should be defined as a top level field on Segmentation */
  behavior?: Behavior;

  mappedDataGroupId?: string;
  // ReferencedBy is generated on the backend and can be utilized
  // to avoid cyclical references
  referencedDirectlyBy?: number[];
  referencedBy?: number[];
  referencedRawEventProperties?: string[];

  propertyType?: PropertyType;
  joinPropertyType?: PropertyType;
  scdEvent?: string;
  user?: number; // TODO @evnp - eliminate this confusing attr
  userId?: number;
  userName?: string;
  userEmail?: string;
  userInfo?: TODO;

  // customPropertiesStore holds the assumption that key (customPropertyId) of a
  // customProperty never changes, but this is no longer true now that we give
  // an inline custom properties a session only ID, from which the user can
  // convert to a real customPropertyId by 'save permanently'. We therefore need
  // a way to communicate to listeners of the store that the old Id is invalid
  // and here is the new Id
  newCustomPropertyId?: number;

  // permissions
  can_view?: boolean;
  canUpdateBasic?: boolean;
  can_share?: boolean;
  is_superadmin?: boolean;
  allow_staff_override?: boolean;

  // sharing-disabled (visibility controls in create-entity-modal)
  isVisible?: boolean;
  isLocked?: boolean;
  canUpdateLocked?: boolean;
  canUpdateVisible?: boolean;
  canUpdateRestricted?: boolean;
  canUpdateVisibility?: boolean;
};
